import type { ComponentProps } from 'react';
import type { Badge } from '@/components/UI';
import type { ScreenerOptimizerResultFoldsRow } from '@/types/appRouter';
import type {
    AuditCandidate,
    FeatureImpactComboRow,
    FeatureImpactExample,
    AuditProfile,
    ExperimentEntitySummary,
    ExternalAuditTool,
    ExtractedRuleCondition,
    McpClientReadiness,
    OptimizerFilterKey,
    OptimizerFilterParseKind,
    OptimizerJobRow,
    OptimizerResultRow,
    OptionObjective,
    OptionTimingEvidenceReadiness,
    OutcomeMetric,
    ValidationStrength
} from './types';
import { OPTIMIZER_RESULT_FILTERS, externalAuditToolOptions, project314McpEndpoint } from './constants';
import { nonNegativeCount } from './materializationProgress';

export { formatCount, formatOptimizerMaterializationPhase, optimizerMaterializationProgress } from './materializationProgress';

export function experimentEntityId(entity: ExperimentEntitySummary): number | null {
    const id = entity.entityId ?? entity.id ?? null;
    if (typeof id !== 'number' || !Number.isFinite(id) || id <= 0) return null;
    return Math.floor(id);
}

export function labelForExternalAuditTool(tool: ExternalAuditTool): string {
    return externalAuditToolOptions.find((option) => option.value === tool)?.label ?? 'External AI';
}

export function badgeVariantForMcpStatus(status: string | undefined): ComponentProps<typeof Badge>['variant'] {
    if (status === 'connected') return 'success';
    if (status === 'no_token' || status === 'token_waiting' || status === 'connector_required') return 'warning';
    return 'secondary';
}

export function optionTimingEvidenceBadgeState(
    readiness: OptionTimingEvidenceReadiness | null,
    isLoading: boolean
): { label: string; message: string; variant: ComponentProps<typeof Badge>['variant'] } {
    if (isLoading) {
        return {
            label: 'Checking optional Timing Edge…',
            message: 'Checking whether this optimizer run contains Option Timing Edge evidence.',
            variant: 'info'
        };
    }

    if (!readiness) {
        return {
            label: 'Timing Edge status unavailable (optional)',
            message: 'Project314 could not load optional Timing Edge status. You can still generate a recommendation.',
            variant: 'secondary'
        };
    }

    const variant: ComponentProps<typeof Badge>['variant'] = readiness.status === 'ready' ? 'success' : 'secondary';

    return { label: readiness.label, message: readiness.message, variant };
}

export function buildExternalAuditSetupText(tool: ExternalAuditTool): string {
    if (tool === 'codex') {
        return `export PROJECT314_MCP_TOKEN='paste-token-here'\ncodex mcp add project314 --url ${project314McpEndpoint} --bearer-token-env-var PROJECT314_MCP_TOKEN`;
    }

    if (tool === 'claude_code') {
        return `export PROJECT314_MCP_TOKEN='paste-token-here'\nclaude mcp add --transport http --scope user project314 ${project314McpEndpoint} --header "Authorization: Bearer \${PROJECT314_MCP_TOKEN}"`;
    }

    if (tool === 'chatgpt' || tool === 'claude') {
        return `${labelForExternalAuditTool(tool)} requires a remote MCP app/connector before it can call Project314 tools.\nProject314 MCP endpoint: ${project314McpEndpoint}`;
    }

    return `Create a Project314 MCP token in Developer Access, then configure ${labelForExternalAuditTool(tool)} with ${project314McpEndpoint}.`;
}

export function buildExternalAuditReadinessSetupText(params: {
    readiness: McpClientReadiness | undefined;
    tool: ExternalAuditTool;
}): string {
    const setupInstructions = params.readiness?.connector.setupInstructions;

    return Array.isArray(setupInstructions) ? setupInstructions.join('\n') : buildExternalAuditSetupText(params.tool);
}

export function toNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

export function formatPct01(value: string | number, decimals: number = 1): string {
    const pct = toNumber(value) * 100;
    return `${pct.toFixed(decimals)}%`;
}

export function formatNullablePct01(value: string | number | null | undefined, decimals: number = 1): string {
    if (value === null || value === undefined) return '-';
    return formatPct01(value, decimals);
}

export function formatDecimal(value: string | number | null | undefined, decimals: number = 2): string {
    const n = toNumber(value);
    return n.toFixed(decimals);
}

export function profitColorClass(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return 'text-zinc-400';
    const n = toNumber(value);
    if (n === 0) return 'text-zinc-400';
    return n > 0 ? 'text-green-400' : 'text-red-400';
}

export function clamp01Number(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
}

export function formatPct01Number(value: number, decimals: number = 1): string {
    if (!Number.isFinite(value)) return '-';
    return `${(clamp01Number(value) * 100).toFixed(decimals)}%`;
}

export function extractOptimizerConditionKeys(computedRuleSummary: string): string[] {
    const conditionKeys = new Set<string>();
    const re = /([A-Za-z0-9_.]+)\s*(<=|>=|!=|=|<|>|any|empty|not_empty)(?=\s|\(|\)|$)/g;

    let match: RegExpExecArray | null;

    while ((match = re.exec(computedRuleSummary)) !== null) {
        const featureKey = match[1];
        const operator = match[2];
        if (!featureKey || !operator) continue;
        conditionKeys.add(`${featureKey}${operator}`);
    }

    const uniqueSorted = Array.from(conditionKeys);
    uniqueSorted.sort((a, b) => a.localeCompare(b));
    return uniqueSorted;
}

export function jaccardSimilaritySortedUnique(a: string[], b: string[]): number {
    if (a.length === 0 && b.length === 0) return 1;
    if (a.length === 0 || b.length === 0) return 0;

    let i = 0;
    let j = 0;
    let intersection = 0;

    while (i < a.length && j < b.length) {
        const av = a[i];
        const bv = b[j];

        if (av === bv) {
            intersection += 1;
            i += 1;
            j += 1;
            continue;
        }

        if (av < bv) i += 1;
        else j += 1;
    }

    const union = a.length + b.length - intersection;
    return union > 0 ? intersection / union : 0;
}

export function computeValidationStrength(row: OptimizerResultRow): ValidationStrength {
    const cvSign = toNumber(row.cvSignAgreement);
    const hoLift = toNumber(row.holdoutLift);
    if (cvSign >= 0.8 && hoLift > 0) return 'strong';
    if (cvSign >= 0.6 || hoLift > 0) return 'moderate';
    return 'weak';
}

export function validationBadgeVariant(strength: ValidationStrength): ComponentProps<typeof Badge>['variant'] {
    switch (strength) {
        case 'strong':
            return 'success';
        case 'moderate':
            return 'warning';
        case 'weak':
            return 'danger';
    }
}

export function formatSignedPct(value: number, decimals: number = 2): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(decimals)}%`;
}

export function parseFiniteNumberString(value: string | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function parseOptionalNumberFilter(value: string): number | undefined {
    if (value.trim() === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseOptionalPercentFilter(value: string): number | undefined {
    const parsed = parseOptionalNumberFilter(value);
    return parsed === undefined ? undefined : parsed / 100;
}

export function parseOptionalPositiveIntegerFilter(value: string): number | undefined {
    const parsed = parseOptionalNumberFilter(value);
    if (parsed === undefined) return undefined;
    const floored = Math.max(0, Math.floor(parsed));
    return floored > 0 ? floored : undefined;
}

export function normalizeNumberInput(raw: string, options: { integer?: boolean; min?: number; max?: number } = {}): string | null {
    if (raw.trim() === '') return '';

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;

    let normalized = options.integer ? Math.floor(parsed) : parsed;

    if (options.min !== undefined) normalized = Math.max(options.min, normalized);
    if (options.max !== undefined) normalized = Math.min(options.max, normalized);

    return String(normalized);
}

export function createEmptyOptimizerFilterValues(): Record<OptimizerFilterKey, string> {
    return Object.fromEntries(OPTIMIZER_RESULT_FILTERS.map((def) => [def.key, ''])) as Record<OptimizerFilterKey, string>;
}

export function parseOptimizerFilterValue(parse: OptimizerFilterParseKind, value: string): number | undefined {
    switch (parse) {
        case 'resultId': {
            if (value.trim() === '') return undefined;
            const parsed = Number(value);
            if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
            return Math.floor(parsed);
        }

        case 'positiveInt':
            return parseOptionalPositiveIntegerFilter(value);
        case 'number':
            return parseOptionalNumberFilter(value);
        case 'percent':
            return parseOptionalPercentFilter(value);
    }
}

export function formatDollarPnL(dollarValue: number): string {
    const sign = dollarValue >= 0 ? '+' : '';
    const abs = Math.abs(dollarValue);

    if (abs >= 1_000_000) {
        return `${sign}$${(dollarValue / 1_000_000).toFixed(1)}M`;
    }

    if (abs >= 1_000) {
        return `${sign}$${Math.round(dollarValue).toLocaleString()}`;
    }

    return `${sign}$${dollarValue.toFixed(0)}`;
}

export function computePosCapturedFromFold(fold: ScreenerOptimizerResultFoldsRow): number {
    const total = fold.totalPosSum;
    if (!Number.isFinite(total) || total <= 0) return 0;
    return clamp01Number(fold.passPosSum / total);
}

export function computeNegAvoidedFromFold(fold: ScreenerOptimizerResultFoldsRow): number {
    const total = fold.totalNegAbsSum;
    if (!Number.isFinite(total) || total <= 0) return 0;
    return clamp01Number(1 - fold.passNegAbsSum / total);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function safeJsonParse(raw: string): unknown | null {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function extractConditionsFromRuleJson(ruleJson: string | null | undefined): ExtractedRuleCondition[] {
    if (!ruleJson) return [];
    const parsed = safeJsonParse(ruleJson);
    if (!parsed) return [];

    const out: ExtractedRuleCondition[] = [];

    const visit = (node: unknown) => {
        if (!isRecord(node)) return;

        if (typeof node.featureKey === 'string') {
            out.push({
                featureKey: node.featureKey,
                operator: typeof node.operator === 'string' ? node.operator : undefined,
                value: node.value,
                duration: node.duration,
                lookbackBars: node.lookbackBars,
                missingPolicy: node.missingPolicy
            });
        }

        const { children } = node;

        if (Array.isArray(children)) {
            for (const child of children) visit(child);
        }

        const { conditions } = node;

        if (Array.isArray(conditions)) {
            for (const child of conditions) visit(child);
        }
    };

    if (isRecord(parsed) && parsed.rootGroup) {
        visit(parsed.rootGroup);
        return out;
    }

    visit(parsed);
    return out;
}

export function formatCondition(cond: ExtractedRuleCondition): string {
    const op = cond.operator ? ` ${cond.operator} ` : ' ';

    const value =
        cond.value === undefined
            ? '?'
            : typeof cond.value === 'string' || typeof cond.value === 'number' || typeof cond.value === 'boolean'
              ? String(cond.value)
              : JSON.stringify(cond.value);

    const extras: string[] = [];
    if (typeof cond.duration === 'string' && cond.duration.length > 0) extras.push(`dur=${cond.duration}`);
    if (typeof cond.lookbackBars === 'number') extras.push(`lb=${cond.lookbackBars}`);
    if (typeof cond.missingPolicy === 'string' && cond.missingPolicy.length > 0) extras.push(`missing=${cond.missingPolicy}`);

    const suffix = extras.length > 0 ? ` (${extras.join(', ')})` : '';
    return `${cond.featureKey}${op}${value}${suffix}`;
}

export function badgeVariantForJobStatus(status: OptimizerJobRow['status']): ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case 'queued':
            return 'warning';
        case 'materializing':
        case 'optimizing':
        case 'validating':
            return 'info';
        case 'completed':
            return 'success';
        case 'failed':
            return 'danger';
        case 'cancelled':
            return 'secondary';
        default:
            return 'secondary';
    }
}

export function walkForwardBadgeState(
    job: Pick<
        OptimizerJobRow,
        | 'status'
        | 'validationWindowStage'
        | 'validationDatasetId'
        | 'validationDatasetStatus'
        | 'validationDatasetRowCount'
        | 'validationDatasetBoundaryPurgedRowCount'
        | 'validationDatasetUnresolvedOutcomeRowCount'
    >
): { label: string; detail: string; variant: ComponentProps<typeof Badge>['variant'] } {
    if (job.status === 'completed' && job.validationDatasetId == null) {
        return {
            label: 'Legacy — rerun required',
            detail: 'This optimizer job predates sealed walk-forward datasets and cannot start a new AI analysis.',
            variant: 'warning'
        };
    }

    switch (job.validationWindowStage) {
        case 'dataset_pending':
            return {
                label: 'Building',
                detail: 'Walk-forward dataset is materializing in this job',
                variant: 'warning'
            };
        case 'ready':
            return {
                label: 'Ready',
                detail:
                    `Dataset #${job.validationDatasetId ?? '?'} · ${(job.validationDatasetRowCount ?? 0).toLocaleString()} rows · ` +
                    `${(job.validationDatasetBoundaryPurgedRowCount ?? 0).toLocaleString()} boundary-purged · ` +
                    `${(job.validationDatasetUnresolvedOutcomeRowCount ?? 0).toLocaleString()} unresolved`,
                variant: 'success'
            };
        case 'failed':
            return {
                label: 'Failed',
                detail: `Dataset #${job.validationDatasetId ?? '?'} · ${job.validationDatasetStatus ?? 'unavailable'}`,
                variant: 'danger'
            };
        case undefined:
            return { label: 'Unavailable', detail: 'Refresh after the backend deployment completes', variant: 'secondary' };
    }
}

export function badgeVariantForWorkerLiveness(status: OptimizerJobRow['workerLivenessStatus']): ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case 'active':
            return 'info';
        case 'recovering':
            return 'warning';
        case 'stalled':
            return 'danger';
        case null:
        case undefined:
            return 'secondary';
    }
}

export function labelForWorkerLiveness(job: Pick<OptimizerJobRow, 'workerLivenessStatus' | 'workerRecoveryCount'>): string | null {
    const recoveryCount = job.workerRecoveryCount ?? 0;

    switch (job.workerLivenessStatus) {
        case 'active':
            return 'worker active';
        case 'recovering':
            return `recovering${recoveryCount > 0 ? ` · attempt ${recoveryCount}` : ''}`;
        case 'stalled':
            return 'worker stalled';
        case null:
        case undefined:
            return null;
    }
}

export function badgeVariantForStrategyExitOutcomeSetStatus(
    status: OptimizerJobRow['strategyExitOutcomeSetStatus']
): ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case null:
            return 'secondary';
        case 'ready':
            return 'success';
        case 'queued':
        case 'materializing':
            return 'warning';
        case 'failed':
            return 'danger';
        case 'cancelled':
        case 'stale':
            return 'secondary';
        default:
            return 'secondary';
    }
}

export function optimizerAnalysisSkippedColumns(job: OptimizerJobRow): number | null {
    const skippedCounts = [
        nonNegativeCount(job.analysisSuppressedColumns),
        nonNegativeCount(job.analysisSkippedNoPresentColumns),
        nonNegativeCount(job.analysisSkippedHighMissingColumns),
        nonNegativeCount(job.analysisSkippedLowVarianceColumns)
    ];

    const presentCounts = skippedCounts.filter((count): count is number => count !== null);
    if (presentCounts.length === 0) return null;
    return presentCounts.reduce((sum, count) => sum + count, 0);
}

export function badgeVariantForOutcomeMetric(metric: OutcomeMetric | null | undefined): ComponentProps<typeof Badge>['variant'] {
    return metric === 'option_profit_percent' ? 'warning' : 'secondary';
}

export function badgeVariantForAuditGrade(grade: AuditCandidate['robustness']['grade']): ComponentProps<typeof Badge>['variant'] {
    switch (grade) {
        case 'excellent':
        case 'strong':
            return 'success';
        case 'watchlist':
            return 'warning';
        case 'fragile':
        case 'reject':
            return 'danger';
    }
}

export function labelForOutcomeMetric(metric: OutcomeMetric | null | undefined): string {
    return metric === 'option_profit_percent' ? 'Options P&L' : 'Underlying P&L';
}

export function labelForOptionObjective(objective: OptionObjective | null | undefined): string {
    return objective === 'executed_only' ? 'Executed only' : 'EV per signal';
}

export function helpTextForOptionObjective(objective: OptionObjective | null | undefined): string {
    return objective === 'executed_only'
        ? 'Scores only signals with an executed option trade; unfilled signals are ignored. Can overstate results when fills are rare.'
        : 'Scores every signal; signals with no executed option count as 0% P&L. Rewards rules that both fire and fill.';
}

export function labelForAuditProfile(profile: AuditProfile): string {
    return profile === 'liquid_large_cap_options' ? 'Large-cap options' : 'General';
}

export function formatMarketCap(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '-';
    if (value >= 1_000_000_000_000) return `$${(value / 1_000_000_000_000).toFixed(2)}T`;
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
}

export function buildComboFeatureRows(results: OptimizerResultRow[], featureImpactTopN: number): FeatureImpactComboRow[] {
    if (results.length === 0) return [];
    const n = Math.max(1, Math.min(featureImpactTopN, results.length));
    const topResults = results.slice(0, n);

    const byFeature = new Map<
        string,
        {
            inTopResultsCount: number;
            conditionOccurrences: number;
            liftSum: number;
            liftCount: number;
            holdoutLiftSum: number;
            holdoutLiftCount: number;
            sweetSum: number;
            sweetCount: number;
            operatorCounts: Map<string, number>;
            examples: FeatureImpactExample[];
        }
    >();

    const ensure = (featureKey: string) => {
        const existing = byFeature.get(featureKey);
        if (existing) return existing;

        const created = {
            inTopResultsCount: 0,
            conditionOccurrences: 0,
            liftSum: 0,
            liftCount: 0,
            holdoutLiftSum: 0,
            holdoutLiftCount: 0,
            sweetSum: 0,
            sweetCount: 0,
            operatorCounts: new Map<string, number>(),
            examples: [] as FeatureImpactExample[]
        };

        byFeature.set(featureKey, created);
        return created;
    };

    for (const result of topResults) {
        const conds = extractConditionsFromRuleJson(result.ruleJson);
        if (conds.length === 0) continue;

        const lift = parseFiniteNumberString(result.lift);
        const holdoutLift = parseFiniteNumberString(result.holdoutLift);
        const sweetScore = parseFiniteNumberString(result.sweetScore);

        const unique = new Set<string>();

        for (const c of conds) {
            unique.add(c.featureKey);
            const stats = ensure(c.featureKey);
            stats.conditionOccurrences += 1;

            if (c.operator) {
                stats.operatorCounts.set(c.operator, (stats.operatorCounts.get(c.operator) ?? 0) + 1);
            }
        }

        for (const featureKey of unique) {
            const stats = ensure(featureKey);
            stats.inTopResultsCount += 1;

            if (lift !== null) {
                stats.liftSum += lift;
                stats.liftCount += 1;
            }

            if (holdoutLift !== null) {
                stats.holdoutLiftSum += holdoutLift;
                stats.holdoutLiftCount += 1;
            }

            if (sweetScore !== null) {
                stats.sweetSum += sweetScore;
                stats.sweetCount += 1;
            }

            if (stats.examples.length < 6) {
                const matched = conds.filter((c) => c.featureKey === featureKey);

                for (const mc of matched.slice(0, 2)) {
                    if (stats.examples.length >= 6) break;

                    stats.examples.push({
                        resultId: result.id,
                        resultScore: result.selectionScore !== null ? toNumber(result.selectionScore) : null,
                        condition: formatCondition(mc),
                        lift,
                        holdoutLift,
                        sweetScore
                    });
                }
            }
        }
    }

    const toTopOperator = (m: Map<string, number>): string | null => {
        let best: { op: string; count: number } | null = null;

        for (const [op, count] of m.entries()) {
            if (!best || count > best.count) best = { op, count };
        }

        return best?.op ?? null;
    };

    return Array.from(byFeature.entries())
        .map(([featureKey, stats]) => {
            const avgLift = stats.liftCount > 0 ? stats.liftSum / stats.liftCount : null;
            const avgHoldoutLift = stats.holdoutLiftCount > 0 ? stats.holdoutLiftSum / stats.holdoutLiftCount : null;
            const avgSweetScore = stats.sweetCount > 0 ? stats.sweetSum / stats.sweetCount : null;
            return {
                featureKey,
                inTopResultsCount: stats.inTopResultsCount,
                topResultsN: n,
                conditionOccurrences: stats.conditionOccurrences,
                avgLift,
                avgHoldoutLift,
                avgSweetScore,
                topOperator: toTopOperator(stats.operatorCounts),
                examples: stats.examples
            };
        })
        .sort((a, b) => {
            if (b.inTopResultsCount !== a.inTopResultsCount) return b.inTopResultsCount - a.inTopResultsCount;
            const bHo = b.avgHoldoutLift ?? Number.NEGATIVE_INFINITY;
            const aHo = a.avgHoldoutLift ?? Number.NEGATIVE_INFINITY;
            if (bHo !== aHo) return bHo - aHo;
            const bLift = b.avgLift ?? Number.NEGATIVE_INFINITY;
            const aLift = a.avgLift ?? Number.NEGATIVE_INFINITY;
            return bLift - aLift;
        });
}
